import React, { useEffect, useState } from "react";
import type { SimpleSettings } from "@juro/shared";
import { btnStyle } from "../../../App.js";

type SimpleTemplateDrawerProps = {
  open: boolean;
  docTypeLabel: string;
  templateName?: string;
  /** テンプレートに保存済みの簡易設定（未設定なら undefined） */
  settings?: SimpleSettings;
  saving?: boolean;
  onSave: (settings: SimpleSettings) => void;
  onClose: () => void;
  onOpenAdvanced: () => void;
};

// フォームの既定値（未設定テンプレートで表示する初期プレースホルダー相当の値）
const DEFAULTS = {
  office_name: "登記測量 長坂・天野合同事務所",
  postal: "444-0853",
  tel: "0564-52-8530",
  fax: "0564-51-7996",
  address: "岡崎市三崎町9番地5",
  honorific: "様",
  footer_note: "※振込手数料はお客様にてご負担願います。",
  col_date: "日付",
  col_description: "種別",
  col_fee: "報酬額",
  col_tax: "登録税又は印紙税",
} as const;

const inputStyle: React.CSSProperties = {
  padding: "7px 10px",
  border: "1px solid #cbd5e1",
  borderRadius: 6,
  fontSize: 13,
  width: "100%",
  boxSizing: "border-box",
  background: "#fff",
};

const sectionTitleStyle: React.CSSProperties = {
  fontSize: 13,
  fontWeight: 800,
  color: "#0f172a",
  margin: "18px 0 10px",
};

const labelStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: 4,
  fontSize: 12,
  fontWeight: 700,
  color: "#475569",
};

export function SimpleTemplateDrawer({
  open,
  docTypeLabel,
  templateName,
  settings,
  saving,
  onSave,
  onClose,
  onOpenAdvanced,
}: SimpleTemplateDrawerProps) {
  const [officeName, setOfficeName] = useState("");
  const [postal, setPostal] = useState("");
  const [tel, setTel] = useState("");
  const [fax, setFax] = useState("");
  const [address, setAddress] = useState("");
  const [titleText, setTitleText] = useState("");
  const [honorific, setHonorific] = useState("");
  const [footerNote, setFooterNote] = useState("");
  const [colDate, setColDate] = useState("");
  const [colDescription, setColDescription] = useState("");
  const [colFee, setColFee] = useState("");
  const [colTax, setColTax] = useState("");

  // 開くたびに保存済み設定（無ければ既定値）でフォームを初期化する
  useEffect(() => {
    if (!open) return;
    setOfficeName(settings?.office?.name ?? DEFAULTS.office_name);
    setPostal(settings?.office?.postal ?? DEFAULTS.postal);
    setTel(settings?.office?.tel ?? DEFAULTS.tel);
    setFax(settings?.office?.fax ?? DEFAULTS.fax);
    setAddress(settings?.office?.address ?? DEFAULTS.address);
    setTitleText(settings?.title_text ?? (docTypeLabel.includes("請求書") ? "請求書" : docTypeLabel));
    setHonorific(settings?.honorific ?? DEFAULTS.honorific);
    setFooterNote(settings?.boilerplate?.footer_note ?? DEFAULTS.footer_note);
    setColDate(settings?.column_labels?.date ?? DEFAULTS.col_date);
    setColDescription(settings?.column_labels?.description ?? DEFAULTS.col_description);
    setColFee(settings?.column_labels?.fee ?? DEFAULTS.col_fee);
    setColTax(settings?.column_labels?.tax ?? DEFAULTS.col_tax);
  }, [open, settings, docTypeLabel]);

  if (!open) return null;

  const handleSave = () => {
    onSave({
      office: {
        name: officeName.trim(),
        postal: postal.trim(),
        tel: tel.trim(),
        fax: fax.trim(),
        address: address.trim(),
      },
      title_text: titleText.trim(),
      honorific: honorific.trim(),
      boilerplate: { footer_note: footerNote.trim() },
      column_labels: {
        date: colDate.trim(),
        description: colDescription.trim(),
        fee: colFee.trim(),
        tax: colTax.trim(),
      },
    });
  };

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 1000 }}>
      <div
        onClick={onClose}
        style={{ position: "absolute", inset: 0, background: "rgba(15, 23, 42, 0.28)" }}
      />
      <aside
        role="dialog"
        aria-modal="true"
        aria-label="体裁を整える"
        style={{
          position: "absolute",
          top: 0,
          right: 0,
          width: "min(520px, 100vw)",
          height: "100%",
          background: "#fff",
          boxShadow: "-12px 0 32px rgba(15, 23, 42, 0.18)",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <header style={{ padding: "18px 20px 14px", borderBottom: "1px solid #e2e8f0" }}>
          <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 16 }}>
            <div>
              <h2 style={{ margin: 0, fontSize: 18, fontWeight: 900, color: "#0f172a" }}>
                体裁を整える
              </h2>
              <p style={{ margin: "6px 0 0", fontSize: 12, lineHeight: 1.6, color: "#64748b" }}>
                この設定は〈{docTypeLabel}〉の今後の作成分に適用されます。発行済みの書類は変わりません。
              </p>
            </div>
            <button
              type="button"
              onClick={onClose}
              aria-label="閉じる"
              style={{
                width: 32,
                height: 32,
                border: "1px solid #e2e8f0",
                borderRadius: 6,
                background: "#fff",
                cursor: "pointer",
                color: "#475569",
                fontSize: 18,
                lineHeight: "28px",
              }}
            >
              ×
            </button>
          </div>
        </header>

        <div style={{ padding: "0 20px 20px", overflowY: "auto", flex: 1 }}>
          {templateName && (
            <div style={{
              marginTop: 16,
              padding: "10px 12px",
              background: "#f8fafc",
              border: "1px solid #e2e8f0",
              borderRadius: 8,
              color: "#475569",
              fontSize: 12,
            }}>
              現在の体裁: {templateName}
            </div>
          )}

          <div style={sectionTitleStyle}>事務所情報</div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            <label style={{ ...labelStyle, gridColumn: "1 / -1" }}>
              事務所名
              <input value={officeName} onChange={(e) => setOfficeName(e.target.value)} style={inputStyle} />
            </label>
            <label style={labelStyle}>
              郵便番号
              <input value={postal} onChange={(e) => setPostal(e.target.value)} style={inputStyle} />
            </label>
            <label style={labelStyle}>
              TEL
              <input value={tel} onChange={(e) => setTel(e.target.value)} style={inputStyle} />
            </label>
            <label style={labelStyle}>
              FAX
              <input value={fax} onChange={(e) => setFax(e.target.value)} style={inputStyle} />
            </label>
            <label style={{ ...labelStyle, gridColumn: "1 / -1" }}>
              住所
              <input value={address} onChange={(e) => setAddress(e.target.value)} style={inputStyle} />
            </label>
          </div>

          <div style={sectionTitleStyle}>表記</div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            <label style={labelStyle}>
              タイトル
              <input value={titleText} onChange={(e) => setTitleText(e.target.value)} style={inputStyle} />
            </label>
            <label style={labelStyle}>
              敬称（空欄で非表示）
              <input value={honorific} onChange={(e) => setHonorific(e.target.value)} style={inputStyle} />
            </label>
          </div>

          <div style={sectionTitleStyle}>定型文</div>
          <label style={labelStyle}>
            備考・振込案内の既定文
            <textarea
              value={footerNote}
              onChange={(e) => setFooterNote(e.target.value)}
              rows={3}
              style={{ ...inputStyle, resize: "vertical", fontFamily: "inherit", lineHeight: 1.6 }}
            />
          </label>

          <div style={sectionTitleStyle}>明細表の列名</div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            <label style={labelStyle}>
              日付
              <input value={colDate} onChange={(e) => setColDate(e.target.value)} style={inputStyle} />
            </label>
            <label style={labelStyle}>
              内容
              <input value={colDescription} onChange={(e) => setColDescription(e.target.value)} style={inputStyle} />
            </label>
            <label style={labelStyle}>
              報酬
              <input value={colFee} onChange={(e) => setColFee(e.target.value)} style={inputStyle} />
            </label>
            <label style={labelStyle}>
              立替・登録税
              <input value={colTax} onChange={(e) => setColTax(e.target.value)} style={inputStyle} />
            </label>
          </div>
        </div>

        <footer style={{
          padding: 16,
          borderTop: "1px solid #e2e8f0",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 12,
        }}>
          <button type="button" style={btnStyle("ghost")} onClick={onOpenAdvanced}>
            詳細設定（上級者向け）→
          </button>
          <div style={{ display: "flex", gap: 8 }}>
            <button type="button" style={btnStyle("ghost")} onClick={onClose}>
              キャンセル
            </button>
            <button type="button" style={btnStyle("primary")} onClick={handleSave} disabled={saving}>
              {saving ? "保存中…" : "保存"}
            </button>
          </div>
        </footer>
      </aside>
    </div>
  );
}
